// #67 Gate (DC-22): one readiness card for Offer readiness, Debrief readiness, the coverage validator, Search
// Readiness, Record outcome, Release. The head is the verdict (status pill + the count of rows still open), the body
// is check rows each deep-linked to the control that clears it, and the foot is the one primary action. While a row
// is open the primary is dashed, aria-disabled and described by the first open row itself (DS #67), the row's words
// mirrored in its tooltip; it does nothing. `ready` glows the top rule blue; `ring` adds a percent or n/N ring;
// `below` is the bare form: the list under a blocked button. Loading draws skeleton rows; no rows collapses to one
// ok *Ready*; refused is the red banner at the top of the card (Card's slot).

import type { ReactNode } from "react";
import { Button, Card, CheckRow, Checklist, Count, Pill, type CardRefused, type CheckStatus } from "../core";
import { DEBRIEF_READINESS_STATUS } from "../labels";
import { cx } from "../lib/cx";
import { useSvgId } from "../lib/useSvgId";
import { Ring } from "./Ring";
import type { Hs } from "../lib/hs";

export interface GateRow {
  status: CheckStatus;
  text: string;
  sub?: string | undefined;
  href?: string | undefined;
  /** The row's open items, drawn as a Count after its words. */
  count?: number | undefined;
  hs?: Hs | undefined;
}

export interface GateAction {
  /** His verb (*Release*, *Record outcome*, *Finalize* …). */
  label: string;
  onClick?: (() => void) | undefined;
  busy?: boolean | undefined;
  hs?: Hs | undefined;
}

export interface GateProps {
  /** His card title (*Offer readiness*, *Debrief readiness*, *Search Readiness* …). */
  title?: string | undefined;
  /** The verdict word on the pill (*Ready*, *Not Ready*, *Validation Failed*, *Ready for Verbal Offer* …). */
  verdict?: string | undefined;
  rows: readonly GateRow[];
  action?: GateAction | undefined;
  /** The gate is clear and its action is the page's live one: the blue top rule. */
  ready?: boolean | undefined;
  /** gate.ring: a percent, or n of N. */
  ring?: { pct: number } | { n: number; of: number } | undefined;
  /** The bare form: the list under the blocked button, no card. */
  below?: boolean | undefined;
  loading?: boolean | undefined;
  refused?: CardRefused | undefined;
  /** "view": the verdict and rows, no action. */
  mode?: "edit" | "view" | undefined;
  can?: boolean | undefined;
  className?: string | undefined;
}

export function Gate({ title, verdict, rows, action, ready, ring, below, loading, refused, mode, can, className }: GateProps) {
  const base = useSvgId();
  if (can === false) return null;
  const open = rows.filter((row) => row.status !== "ok");
  const firstOpen = rows.findIndex((row) => row.status !== "ok");
  const rowId = (index: number) => `${base}-gr${index}`;
  const list = (
    <Checklist className="mc-gate-rows">
      {rows.length === 0
        ? <CheckRow status="ok">{DEBRIEF_READINESS_STATUS.ready}</CheckRow>
        : rows.map((row, index) => (
          <CheckRow key={index} status={row.status} sub={row.sub} href={row.href} count={row.count} hs={row.hs} className={cx(index === firstOpen && "mc-gate-first")}>
            <span id={rowId(index)}>{row.text}</span>
          </CheckRow>
        ))}
    </Checklist>
  );
  let primary: ReactNode = null;
  if (action && mode !== "view") {
    const first = rows[firstOpen];
    if (first) {
      primary = <Button tone="primary" blocked={{ reason: first.text, describedBy: rowId(firstOpen) }} hs={action.hs}>{action.label}</Button>;
    } else {
      primary = <Button tone="primary" busy={action.busy} onClick={action.onClick} hs={action.hs}>{action.label}</Button>;
    }
  }
  if (below) {
    return (
      <div className={cx("mc-gate-below", className)}>
        {primary}
        {list}
      </div>
    );
  }
  const glyph = ring ? ("pct" in ring ? <Ring size={56} pct={ring.pct} label={`${title ?? ""} ${ring.pct}%`.trim()} />
    : <Ring size={56} n={ring.n} of={ring.of} label={`${title ?? ""} ${ring.n}/${ring.of}`.trim()} />) : null;
  return (
    <Card
      title={title}
      className={cx("mc-gate", ready && open.length === 0 && "mc-gate-ready", className)}
      pills={verdict ? <Pill>{verdict}</Pill> : undefined}
      count={loading || open.length === 0 ? undefined : open.length}
      loading={loading ? { lines: 3 } : undefined}
      refused={refused}
    >
      <div className={cx("mc-gate-body", glyph !== null && "mc-gate-ringed")}>
        {glyph}
        {list}
      </div>
      {primary ? <div className="mc-gate-foot">{primary}</div> : null}
    </Card>
  );
}
